import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiClient from '@src/api/clients';

const normalizeClients = (raw) => {
  if (!raw) {
    return [];
  }

  const list = Array.isArray(raw) ? raw : raw.results || [];

  return list
    .map((client) => ({
      value: client.id,
      label: client.name,
      feeRate: Number(client.fees) || 0,
    }))
    .filter((client) => client.value !== undefined && client.label);
};

const sortByLabel = (options) =>
  [...options].sort((a, b) => a.label.localeCompare(b.label));

const BASE_PARAMS = { fields: 'id,name,fees', page: 1, page_size: 200, active: 'true' };
const MAX_PAGE_FETCHES = 100;

const useClientAutocomplete = ({ onOptionsChange, parentCompanyId } = {}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const latestRequestId = useRef(0);
  const debounceTimerRef = useRef(null);

  const notifyOptionsChange = useCallback(
    (nextOptions) => {
      onOptionsChange?.(nextOptions);
    },
    [onOptionsChange]
  );

  const setOptionsAndNotify = useCallback(
    (next) => {
      setOptions(next);
      notifyOptionsChange(next);
    },
    [notifyOptionsChange]
  );

  const fetchClients = useCallback(
    async (rawName = '') => {
      const name = rawName?.trim() || '';
      const requestId = latestRequestId.current + 1;
      latestRequestId.current = requestId;
      setLoading(true);

      try {
        const collected = [];
        let requestUrl = 'dashboard/clients/';
        let isFirstRequest = true;
        let pageFetchCount = 0;

        while (requestUrl && pageFetchCount < MAX_PAGE_FETCHES) {
          const requestConfig = {
            url: requestUrl,
            method: 'GET',
          };

          if (isFirstRequest) {
            requestConfig.payload = { ...BASE_PARAMS };
            if (name) {
              requestConfig.payload.name = name;
            }
            if (parentCompanyId) {
              requestConfig.payload.parent_company_id = parentCompanyId;
            }
          }

          const res = await apiClient.request(requestConfig);
          if (latestRequestId.current !== requestId) {
            return;
          }

          const normalizedPage = normalizeClients(res?.data);
          collected.push(...normalizedPage);

          const nextUrl = res?.data?.next;
          if (!nextUrl) {
            break;
          }

          requestUrl = nextUrl;
          isFirstRequest = false;
          pageFetchCount += 1;
        }

        const deduped = Array.from(
          collected.reduce((acc, option) => {
            acc.set(String(option.value), option);
            return acc;
          }, new Map())
            .values()
        );

        setOptionsAndNotify(sortByLabel(deduped));
      } catch (err) {
        if (latestRequestId.current !== requestId) {
          return;
        }
        console.error('Failed to fetch clients', err);
      } finally {
        if (latestRequestId.current === requestId) {
          setLoading(false);
        }
      }
    },
    [setOptionsAndNotify, parentCompanyId]
  );

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const handleInputChange = useCallback(
    (event, value, reason) => {
      if (reason === 'input') {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
          fetchClients(value || '');
        }, 250);
      }
      if (reason === 'clear') {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        fetchClients('');
      }
    },
    [fetchClients]
  );

  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    },
    []
  );

  const addOption = useCallback(
    (option) => {
      if (!option || option.value === undefined || !option.label) {
        return;
      }
      setOptions((prev) => {
        const optionValue = String(option.value);
        if (prev.some((existing) => String(existing.value) === optionValue)) {
          return prev;
        }
        const next = sortByLabel([...prev, option]);
        notifyOptionsChange(next);
        return next;
      });
    },
    [notifyOptionsChange]
  );

  const findOptionByValue = useCallback(
    (value) => {
      if (value === null || value === undefined || value === '') {
        return null;
      }
      const target = String(value);
      return options.find((option) => String(option.value) === target) || null;
    },
    [options]
  );

  const memoizedOptions = useMemo(() => options, [options]);

  return {
    options: memoizedOptions,
    loading,
    handleInputChange,
    addOption,
    findOptionByValue,
    fetchClients,
  };
};

export default useClientAutocomplete;
